import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MarketingScreen } from '../types/campaign';
import { getMarketingScreen } from '../services/couponService';

interface CampaignModalProps {
    isOpen: boolean;
    onClose: () => void;
    couponData: any;
}

export function CampaignModal({
    isOpen,
    onClose,
    couponData,
}: CampaignModalProps) {
    const [videoError, setVideoError] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);
    const videoRef = useRef<HTMLVideoElement>(null);

    const marketingScreen: MarketingScreen | null =
        getMarketingScreen(couponData);

    // Function to determine if a color is bright or dark
    const getTextColorForBackground = (hexColor: string): string => {
        // Remove # if present
        const hex = hexColor.replace('#', '');

        // Convert to RGB
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);

        // Calculate luminance
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

        // Return black text for bright backgrounds, white for dark
        return luminance > 0.5 ? 'black' : 'white';
    };

    // Auto-close modal on escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            // Prevent body scrolling when modal is open
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    // Handle video auto-play
    useEffect(() => {
        if (isOpen && videoRef.current && marketingScreen?.backgroundUri) {
            videoRef.current.play().catch((error) => {
                console.error('Video autoplay failed:', error);
                setVideoError(true);
            });
        }
    }, [isOpen, marketingScreen?.backgroundUri]);

    if (!marketingScreen) {
        return null;
    }

    const toggleFAQ = (index: number) => {
        setExpandedFAQ(expandedFAQ === index ? null : index);
    };

    const parseHTML = (text: string) => {
        // Simple HTML parser for basic tags - be careful with security
        return text
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<ul>/gi, '')
            .replace(/<\/ul>/gi, '')
            .replace(/<li>/gi, '• ')
            .replace(/<\/li>/gi, '\n')
            .replace(/<p>/gi, '')
            .replace(/<\/p>/gi, '\n\n')
            .replace(/<strong>/gi, '')
            .replace(/<\/strong>/gi, '')
            .replace(/<em>/gi, '')
            .replace(/<\/em>/gi, '');
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md"
                        onClick={onClose}
                    />

                    {/* Modal Container - Full screen on mobile, centered on desktop */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ type: 'spring', duration: 0.5 }}
                        className="fixed inset-0 z-50 flex md:items-center md:justify-center md:p-8"
                        onClick={onClose}
                    >
                        {/* Modal Content - Full height on mobile, card on desktop */}
                        <div
                            className="relative w-full h-full md:h-auto md:max-h-[90vh] md:max-w-2xl overflow-y-auto overflow-x-hidden scrollbar-hide"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Main Card Section - Full screen mobile, card desktop */}
                            <div className="bg-black min-h-full md:min-h-0 rounded-b-3xl md:rounded-3xl shadow-2xl overflow-hidden mb-6">
                                {/* Close Button - Positioned to not overlap logo */}
                                <button
                                    onClick={onClose}
                                    className="absolute top-4 right-4 z-30 w-10 h-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all duration-200 hover:scale-110"
                                    aria-label="Close modal"
                                >
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                    >
                                        <path
                                            d="M18 6L6 18M6 6L18 18"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </button>

                                {/* Header with Logo */}
                                {marketingScreen.topImageUri && !imageError && (
                                    <div className="relative bg-black px-8 py-6 flex items-center justify-center">
                                        <img
                                            src={marketingScreen.topImageUri}
                                            alt="Campaign header"
                                            className="max-h-12 w-auto object-contain"
                                            style={{
                                                maxHeight: `${
                                                    marketingScreen.topImageHeight ||
                                                    46
                                                }px`,
                                            }}
                                            onError={() => setImageError(true)}
                                        />
                                    </div>
                                )}

                                {/* Video Container - Portrait Style */}
                                <div className="px-6 md:px-8 pb-6">
                                    <div className="relative rounded-2xl overflow-hidden aspect-[3/4] max-h-[400px] mx-auto bg-gray-900">
                                        {/* Video/Image Background */}
                                        {marketingScreen.backgroundUri &&
                                        !videoError ? (
                                            <video
                                                ref={videoRef}
                                                className="absolute inset-0 w-full h-full object-cover"
                                                autoPlay
                                                muted
                                                loop
                                                playsInline
                                                onError={() =>
                                                    setVideoError(true)
                                                }
                                            >
                                                <source
                                                    src={
                                                        marketingScreen.backgroundUri
                                                    }
                                                    type="video/mp4"
                                                />
                                            </video>
                                        ) : marketingScreen.backgroundThumbnailUri &&
                                          (videoError ||
                                              !marketingScreen.backgroundUri) ? (
                                            <img
                                                src={
                                                    marketingScreen.backgroundThumbnailUri
                                                }
                                                alt="Campaign background"
                                                className="absolute inset-0 w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900" />
                                        )}

                                        {/* Subtle overlay for better contrast if needed */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                                    </div>
                                </div>

                                {/* Content Section */}
                                <div className="px-6 md:px-8 pb-8 text-center">
                                    {/* Title */}
                                    <motion.h1
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{
                                            delay: 0.2,
                                            duration: 0.6,
                                        }}
                                        className="text-2xl md:text-3xl font-bold text-white mb-4 tracking-wide uppercase"
                                    >
                                        {marketingScreen.title}
                                    </motion.h1>

                                    {/* Description */}
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{
                                            delay: 0.3,
                                            duration: 0.6,
                                        }}
                                        className="text-sm md:text-base text-gray-300 mb-8 leading-relaxed max-w-lg mx-auto"
                                    >
                                        {parseHTML(marketingScreen.description)
                                            .split('\n')
                                            .map((line, index) => (
                                                <p
                                                    key={index}
                                                    className={
                                                        index > 0 ? 'mt-3' : ''
                                                    }
                                                >
                                                    {line}
                                                </p>
                                            ))}
                                    </motion.div>

                                    {/* CTA Button */}
                                    <motion.button
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{
                                            delay: 0.4,
                                            duration: 0.6,
                                        }}
                                        onClick={onClose}
                                        className="px-8 py-3 text-base font-semibold rounded-full shadow-lg transition-all duration-300 transform hover:scale-105"
                                        style={{
                                            backgroundColor:
                                                marketingScreen.brandAccentColor ||
                                                '#9333ea',
                                            color: marketingScreen.brandAccentColor
                                                ? getTextColorForBackground(
                                                      marketingScreen.brandAccentColor
                                                  )
                                                : 'white',
                                            filter: 'brightness(1)',
                                        }}
                                        onMouseEnter={(
                                            e: React.MouseEvent<HTMLButtonElement>
                                        ) => {
                                            e.currentTarget.style.filter =
                                                'brightness(1.1)';
                                        }}
                                        onMouseLeave={(
                                            e: React.MouseEvent<HTMLButtonElement>
                                        ) => {
                                            e.currentTarget.style.filter =
                                                'brightness(1)';
                                        }}
                                    >
                                        {marketingScreen.buttonText ||
                                            'Continue'}
                                    </motion.button>
                                </div>
                            </div>

                            {/* FAQ Section - Full width mobile, card desktop */}
                            {marketingScreen.faq &&
                                marketingScreen.faq.questions.length > 0 && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 30 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{
                                            delay: 0.5,
                                            duration: 0.6,
                                        }}
                                        className="md:rounded-3xl overflow-hidden mb-6"
                                    >
                                        <div className="px-6 md:px-8 py-8">
                                            <h2 className="text-xl md:text-2xl font-bold text-white text-center mb-6">
                                                {marketingScreen.faq.title}
                                            </h2>

                                            <div className="space-y-3">
                                                {marketingScreen.faq.questions.map(
                                                    (faqItem, index) => (
                                                        <motion.div
                                                            key={index}
                                                            initial={{
                                                                opacity: 0,
                                                                y: 20,
                                                            }}
                                                            animate={{
                                                                opacity: 1,
                                                                y: 0,
                                                            }}
                                                            transition={{
                                                                delay:
                                                                    0.6 +
                                                                    index * 0.1,
                                                            }}
                                                            className="bg-neutral-800 rounded-xl overflow-hidden border border-neutral-700"
                                                        >
                                                            <button
                                                                onClick={() =>
                                                                    toggleFAQ(
                                                                        index
                                                                    )
                                                                }
                                                                className="w-full px-5 py-4 text-left font-medium text-gray-100 hover:bg-neutral-700/50 transition-colors duration-200 flex items-center justify-between"
                                                            >
                                                                <span className="text-sm md:text-base pr-2">
                                                                    {
                                                                        faqItem.question
                                                                    }
                                                                </span>
                                                                <motion.svg
                                                                    animate={{
                                                                        rotate:
                                                                            expandedFAQ ===
                                                                            index
                                                                                ? 180
                                                                                : 0,
                                                                    }}
                                                                    transition={{
                                                                        duration: 0.2,
                                                                    }}
                                                                    className="w-5 h-5 text-gray-400 flex-shrink-0"
                                                                    fill="none"
                                                                    viewBox="0 0 24 24"
                                                                    stroke="currentColor"
                                                                >
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        strokeWidth="2"
                                                                        d="M19 9l-7 7-7-7"
                                                                    />
                                                                </motion.svg>
                                                            </button>

                                                            <AnimatePresence>
                                                                {expandedFAQ ===
                                                                    index && (
                                                                    <motion.div
                                                                        initial={{
                                                                            height: 0,
                                                                            opacity: 0,
                                                                        }}
                                                                        animate={{
                                                                            height: 'auto',
                                                                            opacity: 1,
                                                                        }}
                                                                        exit={{
                                                                            height: 0,
                                                                            opacity: 0,
                                                                        }}
                                                                        transition={{
                                                                            duration: 0.3,
                                                                        }}
                                                                        className="overflow-hidden"
                                                                    >
                                                                        <div className="px-5 pb-4 text-gray-400 text-sm leading-relaxed border-t border-neutral-700 pt-3">
                                                                            {parseHTML(
                                                                                faqItem.answer
                                                                            )
                                                                                .split(
                                                                                    '\n'
                                                                                )
                                                                                .map(
                                                                                    (
                                                                                        line,
                                                                                        lineIndex
                                                                                    ) => (
                                                                                        <p
                                                                                            key={
                                                                                                lineIndex
                                                                                            }
                                                                                            className={
                                                                                                lineIndex >
                                                                                                0
                                                                                                    ? 'mt-2'
                                                                                                    : ''
                                                                                            }
                                                                                        >
                                                                                            {
                                                                                                line
                                                                                            }
                                                                                        </p>
                                                                                    )
                                                                                )}
                                                                        </div>
                                                                    </motion.div>
                                                                )}
                                                            </AnimatePresence>
                                                        </motion.div>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}

export default CampaignModal;
